import { Dispatch, SetStateAction } from "react";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";

export type SetSearchParamsFn = (
  nextInit: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams)
) => void;

export function convertToSearchParams<T>(
  setState: Dispatch<SetStateAction<T>>,
  setSearchParams: SetSearchParamsFn,
  updateParams: (nextValue: T, params: URLSearchParams) => void
): Dispatch<SetStateAction<T>> {
  return (value: SetStateAction<T>) => {
    setState((prev) => {
      const nextValue = typeof value === "function" ? value(prev) : value;

      setSearchParams((prevParams) => {
        const next = new URLSearchParams(prevParams);
        updateParams(nextValue, next);
        return next;
      });

      return nextValue;
    });
  };
}

export function convertStringToParams(
  key: string,
  setState: Dispatch<SetStateAction<string>>,
  setSearchParams: SetSearchParamsFn
): Dispatch<SetStateAction<string>> {
  return convertToSearchParams<string>(
    setState,
    setSearchParams,
    (nextValue, params) => {
      const current = params.get(key) ?? "";
      const normalizedNext = nextValue ?? "";
      const normalizedCurrent = current ?? "";

      if (normalizedNext === normalizedCurrent) return;

      if (!normalizedNext) {
        params.delete(key);
      } else {
        params.set(key, normalizedNext);
      }

      params.set("page", "1");
    }
  );
}

export function convertOptionsToParams<T extends string>(
  key: string,
  setState: Dispatch<SetStateAction<T>>,
  setSearchParams: SetSearchParamsFn
): Dispatch<SetStateAction<T>> {
  return convertToSearchParams<T>(
    setState,
    setSearchParams,
    (nextValue, params) => {
      params.set(key, nextValue as string);
    }
  );
}

export function convertDateRangeToParams(
  fromKey: string,
  toKey: string,
  setState: Dispatch<SetStateAction<DateRange | undefined>>,
  setSearchParams: SetSearchParamsFn
): Dispatch<SetStateAction<DateRange | undefined>> {
  return convertToSearchParams<DateRange | undefined>(
    setState,
    setSearchParams,
    (nextRange, params) => {
      const nextFrom = nextRange?.from
        ? format(nextRange.from, "yyyy-MM-dd")
        : "";
      const nextTo = nextRange?.to
        ? format(nextRange.to, "yyyy-MM-dd")
        : "";

      const curFrom = params.get(fromKey) ?? "";
      const curTo = params.get(toKey) ?? "";

      if (nextFrom === curFrom && nextTo === curTo) return;

      if (nextFrom) params.set(fromKey, nextFrom);
      else params.delete(fromKey);

      if (nextTo) params.set(toKey, nextTo);
      else params.delete(toKey);

      params.set("page", "1");
    }
  );
}

export function convertFilterToParams<T>(
  key: string,
  setState: Dispatch<SetStateAction<T>>,
  setSearchParams: SetSearchParamsFn,
  hasValue: (value: T) => boolean
): Dispatch<SetStateAction<T>> {
  return convertToSearchParams<T>(
    setState,
    setSearchParams,
    (nextValue, params) => {
      const next = hasValue(nextValue)
        ? encodeURIComponent(JSON.stringify(nextValue))
        : "";

      const current = params.get(key) ?? "";

      if (next === current) return;

      if (next) {
        params.set(key, next);
      } else {
        params.delete(key);
      }

      params.set("page", "1");
    }
  );
}

export function convertTabIndexToParams(
  key: string,
  setIndex: Dispatch<SetStateAction<number>>,
  setSearchParams: SetSearchParamsFn
): (index: number) => void {
  return (index: number) => {
    setIndex(index);

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const current = next.get(key) ?? "";

      if (String(index) === current) return next;

      next.set(key, String(index));
      next.set("page", "1");
      return next;
    });
  };
}

export function getNumberParam(
  params: URLSearchParams,
  key: string,
  defaultValue: number = 0
): number {
  const value = params.get(key);
  if (!value) return defaultValue;

  const parsed = Number(value);
  return Number.isNaN(parsed) ? defaultValue : parsed;
}

export function getStringParam(
  params: URLSearchParams,
  key: string,
  defaultValue: string = ""
): string {
  return params.get(key) ?? defaultValue;
}

export function getOptionParam(
  params: URLSearchParams,
  key: string,
  allowed: any,
  defaultValue: any
) {
  const value = params.get(key);

  if (value && allowed.includes(value)) {
    return value;
  }

  return defaultValue;
}

export function getDateRangeParam(
  params: URLSearchParams,
  fromKey: string,
  toKey: string
): DateRange | undefined {
  const from = params.get(fromKey);
  const to = params.get(toKey);

  if (!from && !to) return undefined;

  const fromDate = from ? new Date(from) : undefined;
  const toDate = to ? new Date(to) : fromDate;

  return { from: fromDate, to: toDate };
}

export function getFilterParam<T>(
  params: URLSearchParams,
  key: string,
  defaultValue: T
): T {
  const value = params.get(key);
  if (!value) return defaultValue;

  try {
    return {
      ...defaultValue,
      ...JSON.parse(decodeURIComponent(value)),
    };
  } catch {
    return defaultValue;
  }
}
